import type StoreService from '@ember-data/store';
import type DraftDocumentModel from 'garaje/models/draft-document';

export interface RequestUploadLinkParams {
  templateId: string;
  locationId?: string | null;
  contentType: string;
  byteSize: number;
  timeoutMs?: number;
}

export interface UploadLink {
  draftDocumentId: string;
  uploadLink: string;
}

interface DraftDocumentDocument {
  data: {
    id: string;
    type: 'draft-documents';
    attributes: Record<string, unknown>;
  };
}

/** Abort a stalled upload rather than leaving the promise — and the card — pending forever. */
const UPLOAD_TIMEOUT_MS = 60_000;

/**
 * Bound for the one-shot API requests (upload-link save, confirm POST,
 * field-values PATCH). Ember Data's adapter has no transport-level timeout, so
 * a stalled request would otherwise leave the consuming task `.isRunning`
 * forever — the same failure class `pollDraftDocumentWithDeadline` guards the
 * poll GET against. The underlying request may still settle later; its result
 * is discarded.
 */
export const REQUEST_TIMEOUT_MS = 30_000;

export async function withRequestDeadline<T>(promise: Promise<T>, label: string, deadlineMs: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => {
          // The losing request is discarded; swallow its eventual settlement so a
          // late rejection doesn't surface as an unhandled promise rejection.
          promise.catch(() => {});
          reject(new Error(`${label} timed out`));
        }, deadlineMs);
      }),
    ]);
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
}

function draftDocumentAdapter(store: StoreService) {
  return store.adapterFor('draft-document');
}

/**
 * Stateless transport for the admin pre-reg draft-document flow over the
 * `/api/v3/draft-documents` action endpoints. Plain functions taking `store`
 * (the existing utils idiom — see `notify-workflow.ts`): the flow has no
 * shared state, so per the review on the introducing PR it lives here rather
 * than a singleton service. The consuming component sequences
 * request-link → upload → confirm inside its own ember-concurrency task (so
 * each step is a cancellation point), mirroring the direct-uploader/
 * active-storage shape.
 *
 * Errors propagate to the caller (the card owns loading/error UI); this layer
 * never swallows them or substitutes a silent fallback — including a stalled
 * upload and a parse timeout, which reject rather than hanging.
 */

/**
 * Reserve a draft-document and obtain a write-only upload URL for the file.
 *
 * Creates a `draft-document-upload-link` resource (its adapter's `pathForType`
 * points at the nested `draft-documents/upload-links` collection), so Ember Data
 * serializes the JSON:API envelope — `content-type` / `byte-size` attributes and
 * the `template` / `location` relationships — instead of hand-assembling it. The
 * `template` is required and the `location` (when supplied) are read from the
 * store as relationship references; the card renders the loaded template record,
 * so a missing template is a programmer error, not a runtime fallback.
 */
export async function requestUploadLink(
  store: StoreService,
  { templateId, locationId, contentType, byteSize, timeoutMs = REQUEST_TIMEOUT_MS }: RequestUploadLinkParams,
): Promise<UploadLink> {
  const template = store.peekRecord('custom-user-document-template', templateId);
  if (!template) {
    throw new Error(`Draft document upload-link requires custom-user-document-template ${templateId} in the store`);
  }

  const uploadLinkRecord = store.createRecord('draft-document-upload-link', {
    contentType,
    byteSize,
    template,
  });

  // Only attach the location relationship when a loaded record is available, so the
  // create envelope omits it entirely (rather than sending `data: null`) when absent.
  if (locationId) {
    const location = store.peekRecord('location', locationId);
    if (location) {
      uploadLinkRecord.location = location;
    }
  }

  try {
    await withRequestDeadline(uploadLinkRecord.save(), 'Draft document upload-link save', timeoutMs);

    const draftDocumentId = uploadLinkRecord.belongsTo('draftDocument').id();
    const uploadLink = uploadLinkRecord.uploadLink;
    if (!draftDocumentId || !uploadLink) {
      throw new Error('Draft document upload-link response missing draft-document id or upload-link');
    }
    return { draftDocumentId, uploadLink };
  } finally {
    // The record is a one-shot envelope: once the id + write-capable upload URL are
    // read (or the save/validation failed), don't leave it lingering in the store.
    // A timed-out save is still in flight — Ember Data throws on unload-while-saving;
    // that abandoned record settles with the request and nothing reads it.
    if (!uploadLinkRecord.isSaving) {
      uploadLinkRecord.unloadRecord();
    }
  }
}

/**
 * PUT the raw file to the presigned upload URL via XHR. Deliberately NOT the
 * Ember Data adapter, ajaxFetch, or `fetch`: the target is object storage, not
 * our API, so the request must carry no auth headers — and XHR is what Mirage
 * (Pretender) intercepts in dev/test (native `fetch` escapes the mock). This
 * mirrors the existing active-storage direct-upload path.
 */
export function uploadFile(uploadLink: string, file: File, options?: { signal?: AbortSignal }): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadLink, true);
    xhr.timeout = UPLOAD_TIMEOUT_MS;

    if (file.type) {
      xhr.setRequestHeader('Content-Type', file.type);
    }

    // Settle-once guard: an abort fired after the PUT already settled must not
    // double-reject (the controller is aborted unconditionally on task teardown).
    let settled = false;
    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      fn();
    };

    xhr.onload = () =>
      settle(() => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Draft document upload failed (${xhr.status})`));
        }
      });
    xhr.onerror = () => settle(() => reject(new Error('Draft document upload failed (network error)')));
    xhr.ontimeout = () => settle(() => reject(new Error('Draft document upload timed out')));
    xhr.onabort = () => settle(() => reject(new Error('Draft document upload aborted')));

    // EC cancellation can't reach into a running promise, so the caller's task passes
    // a signal and aborts it on cancel/teardown — otherwise the in-flight presigned
    // PUT would keep uploading (wasted bandwidth, possible orphaned object).
    if (options?.signal) {
      if (options.signal.aborted) {
        settle(() => reject(new Error('Draft document upload aborted')));
        return;
      }
      options.signal.addEventListener(
        'abort',
        () => {
          if (!settled) xhr.abort();
        },
        { once: true },
      );
    }

    xhr.send(file);
  });
}

/**
 * Confirm the uploaded file and begin server-side parsing. Resolves with the
 * draft-document in `processing` (it may already be `completed`).
 *
 * Stays on `adapter.ajax` rather than `apiAction`: the upload-link response links
 * the draft by id only (no `included`), so the draft isn't materialized as a record
 * until this call's `pushPayload`. `apiAction` requires an existing record to build
 * its URL — which doesn't exist yet at confirm-time — so the id-string form is used.
 */
export async function confirmDraftDocument(
  store: StoreService,
  draftDocumentId: string,
  { templateId, timeoutMs = REQUEST_TIMEOUT_MS }: { templateId: string; timeoutMs?: number },
): Promise<DraftDocumentModel> {
  const adapter = draftDocumentAdapter(store);
  const url = `${adapter.buildURL('draft-document', draftDocumentId)}/confirm`;
  // Per the BE contract: a bare resource identifier in `data`, with the
  // template id in `meta` (not a writable attribute). confirm re-reads the
  // draft from the store after pushPayload.
  const payload = (await withRequestDeadline(
    adapter.ajax(url, 'POST', {
      data: {
        data: { type: 'draft-documents', id: draftDocumentId },
        meta: { 'template-id': templateId },
      },
    }),
    'Draft document confirm',
    timeoutMs,
  )) as DraftDocumentDocument;

  store.pushPayload('draft-document', payload);

  const draftDocument = store.peekRecord('draft-document', draftDocumentId);
  if (!draftDocument) {
    throw new Error(`Draft document ${draftDocumentId} missing from store after confirm`);
  }

  return draftDocument;
}

/**
 * Single GET against the draft-document (always reloads from the server).
 */
export async function pollDraftDocument(store: StoreService, draftDocumentId: string): Promise<DraftDocumentModel> {
  return store.findRecord('draft-document', draftDocumentId, { reload: true });
}

/**
 * A single poll bounded by `deadlineMs`. Ember Data's adapter has no
 * transport-level timeout, so a hung GET would otherwise let the caller's
 * top-of-loop deadline check be skipped, hanging the poll loop forever.
 * The timer is always cleared once the race settles.
 */
export async function pollDraftDocumentWithDeadline(
  store: StoreService,
  draftDocumentId: string,
  deadlineMs: number,
): Promise<DraftDocumentModel> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const pollPromise = pollDraftDocument(store, draftDocumentId);
  try {
    return await Promise.race([
      pollPromise,
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => {
          // The losing GET is discarded; swallow its eventual settlement so a late
          // rejection (a hung findRecord that errors after the deadline) doesn't
          // surface as an unhandled promise rejection — mirrors withRequestDeadline.
          pollPromise.catch(() => {});
          reject(new Error(`Draft document ${draftDocumentId} parsing timed out`));
        }, deadlineMs);
      }),
    ]);
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
}
